import { useCallback } from "react";
import { PageEditSessionService } from "utilities/services/notebooks/page-edit-session-service";

interface UseReadonlyChecklistEditGuard {
    guardAction: () => Promise<boolean>;
}

/** This hook ensures users cannot edit a checklist block if another user is currently editing it */
const useReadonlyChecklistEditGuard = (
    pageId: number,
    blockId: number | undefined,
    onRequestEditMode?: (blockId?: number) => void
): UseReadonlyChecklistEditGuard => {
    const { list: listActiveSessions } =
        PageEditSessionService.useListActiveSessions();

    const { refetch } = listActiveSessions(
        { pageId, blockId },
        { enabled: false }
    );

    const guardAction = useCallback(async (): Promise<boolean> => {
        try {
            const response = await refetch();
            const sessions = response.data?.resultObjects;

            if (sessions?.length) {
                onRequestEditMode?.(blockId);
                return false;
            }

            return true;
        } catch {
            // If we can't check the session, allow the action to proceed
            return true;
        }
    }, [blockId, onRequestEditMode, refetch]);

    return { guardAction };
};

export default useReadonlyChecklistEditGuard;
